import { BibleConnections } from './BibleConnections'
import { ReferenceTextarea } from './ReferenceTextarea'
import { applyReference } from '../connectedBible'
import { useState } from 'react'
import {
  AlertCircle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  CheckCheck,
  Copy,
  Flag,
  GitBranch,
  NotebookPen,
  Trash2,
  X,
} from 'lucide-react'
import {
  deriveEdges,
  draftStatuses,
  duplicateNode,
  nodeWordCount,
  projectIssues,
  setNodeKind,
} from '../model'
import type { DraftStatus, StoryNode, StoryProject } from '../types'
import { EffectsEditor } from './StoryLogicEditor'

type Props = {
  project: StoryProject
  node: StoryNode
  tab: 'details' | 'review'
  onTabChange: (tab: 'details' | 'review') => void
  onChange: (project: StoryProject, historyKey?: string) => void
  onSelectNode: (nodeId: string) => void
  onDeleteNode: (nodeId: string) => void
  onClose: () => void
  onOpenVariables: () => void
  onOpenReference: (id: string) => void
}

export function StoryInspector({
  project,
  node,
  tab,
  onTabChange,
  onChange,
  onSelectNode,
  onDeleteNode,
  onClose,
  onOpenVariables,
  onOpenReference,
}: Props) {
  const [confirmation, setConfirmation] = useState<'ending' | 'delete' | null>(
    null,
  )
  const [reviewFilter, setReviewFilter] = useState<'all' | 'flow' | 'draft'>(
    'all',
  )
  const issues = projectIssues(project)
  const visibleIssues = issues.filter(
    (issue) => reviewFilter === 'all' || issue.kind === reviewFilter,
  )
  const incoming = deriveEdges(project).filter(
    (edge) => edge.targetId === node.id,
  )
  const sceneIndex = project.nodes.indexOf(node)
  const finalCount = project.nodes.filter(
    (scene) => scene.status === 'final',
  ).length
  const edit = (changes: Partial<StoryNode>, key: string) =>
    onChange(
      {
        ...project,
        nodes: project.nodes.map((item) =>
          item.id === node.id ? { ...item, ...changes } : item,
        ),
      },
      key + ':' + node.id,
    )
  const markEnding = () =>
    edit(
      setNodeKind(node, node.kind === 'ending' ? 'scene' : 'ending'),
      'scene-kind',
    )
  const reorder = (direction: number) => {
    const nodes = [...project.nodes]
    nodes.splice(sceneIndex, 1)
    nodes.splice(sceneIndex + direction, 0, node)
    onChange({ ...project, nodes }, 'reorder-scene')
  }
  return (
    <aside className="story-inspector" aria-label="Story inspector">
      <header className="inspector-tabs">
        <button
          type="button"
          aria-pressed={tab === 'details'}
          onClick={() => onTabChange('details')}
        >
          <NotebookPen size={15} /> Scene
        </button>
        <button
          type="button"
          aria-pressed={tab === 'review'}
          onClick={() => onTabChange('review')}
        >
          <CheckCheck size={15} /> Review <span>{issues.length}</span>
        </button>
        <button
          type="button"
          className="icon-button"
          title="Close inspector"
          aria-label="Close inspector"
          onClick={onClose}
        >
          <X size={15} />
        </button>
      </header>
      <div className="inspector-scroll">
        {tab === 'details' ? (
          <>
            <div className="inspector-section inspector-intro">
              <span className="eyebrow">SCENE DETAILS</span>
              <h2>{node.title || 'Untitled scene'}</h2>
              <p>The intent behind the words.</p>
            </div>
            <div className="inspector-section scene-properties">
              <label>
                Draft status
                <select
                  aria-label="Scene draft status"
                  value={node.status ?? 'draft'}
                  onChange={(event) =>
                    edit(
                      { status: event.target.value as DraftStatus },
                      'status',
                    )
                  }
                >
                  {draftStatuses.map((status) => (
                    <option value={status.value} key={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Chapter / act
                <input
                  aria-label="Chapter or act"
                  data-live-path={JSON.stringify(['nodes', node.id, 'chapter'])}
                  id="scene-chapter"
                  list="story-chapters"
                  value={node.chapter ?? ''}
                  placeholder="e.g. Act I · The arrival"
                  onChange={(event) =>
                    edit({ chapter: event.target.value }, 'chapter')
                  }
                />
              </label>
              <datalist id="story-chapters">
                {Array.from(
                  new Set(
                    project.nodes.map((scene) => scene.chapter).filter(Boolean),
                  ),
                ).map((chapter) => (
                  <option key={chapter} value={chapter} />
                ))}
              </datalist>
            </div>
            <div className="inspector-section planning-field">
              <label htmlFor="scene-synopsis">
                Scene intention <span>Synopsis</span>
              </label>
              <ReferenceTextarea
                project={project}
                sourceId={'scene:' + node.id}
                onOpenReference={onOpenReference}
                data-live-path={JSON.stringify(['nodes', node.id, 'synopsis'])}
                  id="scene-synopsis"
                value={node.synopsis ?? ''}
                onText={(value, pick) =>
                  onChange(
                    applyReference(
                      {
                        ...project,
                        nodes: project.nodes.map((n) =>
                          n.id === node.id ? { ...n, synopsis: value } : n,
                        ),
                      },
                      'scene:' + node.id,
                      pick,
                    ),
                    'scene:' + node.id + ':synopsis',
                  )
                }
                placeholder="What changes in this scene? What should the player feel?"
                rows={4}
              />
            </div>
            <div className="inspector-section planning-field">
              <label htmlFor="scene-notes">
                Writer’s notes <NotebookPen size={13} />
              </label>
              <ReferenceTextarea
                project={project}
                sourceId={'scene:' + node.id}
                onOpenReference={onOpenReference}
                data-live-path={JSON.stringify(['nodes', node.id, 'notes'])}
                  id="scene-notes"
                value={node.notes ?? ''}
                onText={(value, pick) =>
                  onChange(
                    applyReference(
                      {
                        ...project,
                        nodes: project.nodes.map((n) =>
                          n.id === node.id ? { ...n, notes: value } : n,
                        ),
                      },
                      'scene:' + node.id,
                      pick,
                    ),
                    'scene:' + node.id + ':notes',
                  )
                }
                placeholder="Loose ideas, continuity reminders, things to revisit…"
                rows={5}
              />
              <small>
                Saved with your story. Kept out of the playable script.
              </small>
            </div>
            <div className="inspector-section">
              <BibleConnections
                key={node.id}
                project={project}
                sourceId={'scene:' + node.id}
                onChange={onChange}
                onOpen={onOpenReference}
              />
            </div>
            <details className="inspector-section scene-logic">
              <summary>
                When this scene begins <span>{node.effects?.length || ''}</span>
              </summary>
              <p className="muted-copy">
                Apply these changes each time the player enters this scene,
                before its first line.
              </p>
              <EffectsEditor
                variables={project.variables ?? []}
                effects={node.effects ?? []}
                context="Scene entry"
                onChange={(effects) => edit({ effects }, 'scene-effects')}
                onOpenVariables={onOpenVariables}
              />
            </details>
            <div className="inspector-section scene-connections">
              <div className="section-label">
                <GitBranch size={14} /> Leads into this scene{' '}
                <span>{incoming.length}</span>
              </div>
              {node.id === project.startNodeId && (
                <p className="opening-caption">
                  <Flag size={12} /> This is your story’s opening.
                </p>
              )}
              {incoming.length
                ? incoming.map((edge) => (
                    <button
                      type="button"
                      key={edge.id}
                      onClick={() => onSelectNode(edge.sourceId)}
                    >
                      <span>
                        {
                          project.nodes.find(
                            (scene) => scene.id === edge.sourceId,
                          )?.title
                        }
                        <small>{edge.label || 'Continues here'}</small>
                      </span>
                      <ArrowRight size={13} />
                    </button>
                  ))
                : node.id !== project.startNodeId && (
                    <p className="muted-copy">
                      No scenes lead here yet. Connect it from a choice or
                      continuation.
                    </p>
                  )}
            </div>
            <div className="inspector-section scene-actions">
              <div className="section-label">Scene actions</div>
              <button
                type="button"
                onClick={() => {
                  const next = duplicateNode(project, node.id)
                  onChange(next, 'duplicate-scene')
                  onSelectNode(next.nodes[sceneIndex + 1].id)
                }}
              >
                <Copy size={14} /> Duplicate scene
              </button>
              <div className="scene-order-actions">
                <button
                  type="button"
                  disabled={sceneIndex === 0}
                  onClick={() => reorder(-1)}
                >
                  <ArrowUp size={13} /> Move up
                </button>
                <button
                  type="button"
                  disabled={sceneIndex === project.nodes.length - 1}
                  onClick={() => reorder(1)}
                >
                  <ArrowDown size={13} /> Move down
                </button>
              </div>
              <button
                type="button"
                onClick={() =>
                  node.kind !== 'ending' && (node.nextId || node.choices.length)
                    ? setConfirmation('ending')
                    : markEnding()
                }
              >
                <Flag size={14} />
                {node.kind === 'ending'
                  ? 'Convert to regular scene'
                  : 'Mark as an ending'}
              </button>
              {node.id !== project.startNodeId && (
                <button
                  type="button"
                  onClick={() =>
                    onChange({ ...project, startNodeId: node.id }, 'opening')
                  }
                >
                  <Flag size={14} /> Make opening scene
                </button>
              )}
              <button
                type="button"
                className="danger-action"
                disabled={project.nodes.length === 1}
                onClick={() => setConfirmation('delete')}
              >
                <Trash2 size={14} /> Delete scene
              </button>
              {confirmation && (
                <div className="inline-confirm" role="alert">
                  <strong>
                    {confirmation === 'ending'
                      ? 'End the story here?'
                      : 'Delete this scene?'}
                  </strong>
                  <p>
                    {confirmation === 'ending'
                      ? 'This removes its outgoing routes. Undo can restore them.'
                      : 'Its incoming routes will be disconnected. Undo can restore the scene.'}
                  </p>
                  <div>
                    <button type="button" onClick={() => setConfirmation(null)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirmation === 'ending') markEnding()
                        else onDeleteNode(node.id)
                        setConfirmation(null)
                      }}
                    >
                      {confirmation === 'ending'
                        ? 'Mark ending'
                        : 'Delete scene'}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="inspector-footnote">
              {nodeWordCount(node)} words · {node.blocks.length} blocks
            </div>
          </>
        ) : (
          <>
            <div className="inspector-section inspector-intro">
              <span className="eyebrow">A FRESH PAIR OF EYES</span>
              <h2>Story review</h2>
              <p>Find loose ends before your player does.</p>
              <div className="draft-progress">
                <span>
                  {finalCount} of {project.nodes.length} scenes final
                </span>
                <progress max={project.nodes.length} value={finalCount} />
              </div>
            </div>
            <div className="review-filters">
              {(['all', 'flow', 'draft'] as const).map((filter) => (
                <button
                  type="button"
                  key={filter}
                  aria-pressed={reviewFilter === filter}
                  onClick={() => setReviewFilter(filter)}
                >
                  {filter === 'all'
                    ? 'All notes'
                    : filter === 'flow'
                      ? 'Story flow'
                      : 'Drafting'}
                </button>
              ))}
            </div>
            <div className="review-list">
              {visibleIssues.length ? (
                visibleIssues.map((issue, index) => (
                  <button
                    type="button"
                    className={'review-issue review-issue--' + issue.kind}
                    key={issue.nodeId + ':' + issue.message + index}
                    onClick={() => {
                      onSelectNode(issue.nodeId)
                    }}
                  >
                    <AlertCircle size={16} />
                    <span>
                      <strong>
                        {project.nodes.find(
                          (scene) => scene.id === issue.nodeId,
                        )?.title || 'Untitled scene'}
                      </strong>
                      <p>{issue.message}</p>
                      <small>
                        Open scene <ArrowRight size={11} />
                      </small>
                    </span>
                  </button>
                ))
              ) : (
                <div className="review-clear">
                  <CheckCheck size={28} />
                  <h3>
                    {reviewFilter === 'all'
                      ? 'No loose ends found'
                      : 'Looking good'}
                  </h3>
                  <p>
                    {reviewFilter === 'all'
                      ? 'Your routes connect and every scene has a first line. Play through to check how it feels.'
                      : 'There are no notes in this category.'}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  )
}
